import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ReactNode } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Database,
  GraduationCap,
  Heart,
  ImagePlus,
  MapPin,
  RefreshCw,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDashboard } from '../context/DashboardContext';
import { useEvents } from '../context/EventsContext';
import { eventTypeLabel } from '../types/events';
import { EntityActionsMenu } from '../components/EntityActionsMenu';
import { resizeImageToDataUrl } from '../utils/imageResize';
import { indexOperatorsByRoomId, matchesViewMode } from '../utils/profileFilter';

const AVATAR_COLORS = [
  'bg-rose-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-teal-500',
  'bg-sky-500',
  'bg-indigo-500',
  'bg-purple-500',
  'bg-pink-500',
];

function colorForEmail(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = (hash * 31 + email.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** Nome amigável derivado localmente do e-mail — não é buscado/sincronizado do Firestore. */
function nameFromEmail(email: string): string {
  const handle = email.split('@')[0] ?? email;
  return handle
    .replace(/[._]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function Avatar({
  email,
  className,
  style,
}: {
  email: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={style}
      className={cn(
        'h-7 w-7 rounded-full border-2 border-card flex items-center justify-center text-[11px] font-semibold text-white uppercase shrink-0',
        colorForEmail(email),
        className
      )}
    >
      {email.charAt(0)}
    </div>
  );
}

const MAX_VISIBLE_AVATARS = 5;

function CollaboratorAvatarStack({
  emails,
  expanded,
  onToggle,
}: {
  emails: string[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const visible = emails.slice(0, MAX_VISIBLE_AVATARS);
  const remaining = emails.length - visible.length;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex items-center gap-1.5 rounded-full hover:bg-muted/60 pl-1 pr-2 py-1 -my-1 transition-colors"
    >
      <div className="flex items-center">
        {visible.map((email, idx) => (
          <Tooltip key={email}>
            <TooltipTrigger asChild>
              <Avatar
                email={email}
                className={idx > 0 ? '-ml-2.5' : undefined}
                style={{ zIndex: visible.length - idx }}
              />
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p className="font-medium text-foreground">{nameFromEmail(email)}</p>
              <p className="text-muted-foreground">{email}</p>
            </TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 && (
          <div
            className="h-7 w-7 rounded-full border-2 border-card bg-muted text-muted-foreground flex items-center justify-center text-[10px] font-semibold -ml-2.5 shrink-0"
            style={{ zIndex: 0 }}
          >
            +{remaining}
          </div>
        )}
      </div>
      {expanded ? (
        <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
      ) : (
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </button>
  );
}

function CollaboratorList({ emails }: { emails: string[] }) {
  return (
    <div className="mt-4 pt-4 border-t border-border">
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-3">
        Criado por ({emails.length})
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        {emails.map((email) => (
          <div
            key={email}
            className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2"
          >
            <Avatar email={email} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {nameFromEmail(email)}
              </p>
              <p className="text-xs text-muted-foreground truncate">{email}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatChip({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center h-9 w-9 rounded-full bg-primary/10 text-primary shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
        <p className="text-sm font-semibold text-foreground leading-tight truncate">{value}</p>
      </div>
    </div>
  );
}

const STATS_THROTTLE_MS = 10 * 60_000;

export function EventCoverBanner() {
  const { event, eventProfiles, operators, profilesLoading } = useDashboard();
  const { updateEventPhoto, syncEventStats, deleteEvent, canEditEvent } = useEvents();

  // Totais reais derivados dos perfis sincronizados do Firestore — todo mundo
  // que apareceu no período do evento, independente do tipo (professor, pai,
  // aluno) ou da visão managed/individual selecionada no filtro do dashboard.
  const realParticipants = eventProfiles.length;

  // Quebra por papel dos mesmos `eventProfiles` acima — SEM os filtros de
  // idade/gênero do ProfileFilter (esses só valem pra análise detalhada,
  // não pro total geral do evento, ver comentário de `eventProfiles` em
  // DashboardContext.tsx).
  const operatorsByRoomId = useMemo(() => indexOperatorsByRoomId(operators), [operators]);
  const roleCounts = useMemo(() => {
    let managed = 0;
    let professor = 0;
    let paiMae = 0;
    for (const p of eventProfiles) {
      if (matchesViewMode(p, 'managed', operatorsByRoomId)) managed++;
      else if (matchesViewMode(p, 'professor', operatorsByRoomId)) professor++;
      else if (matchesViewMode(p, 'pai_mae', operatorsByRoomId)) paiMae++;
    }
    return { managed, professor, paiMae };
  }, [eventProfiles, operatorsByRoomId]);
  const realRecords = eventProfiles.reduce((sum, p) => {
    const comm = p.subcategoryClicks.reduce((s, c) => s + c.clickCount, 0);
    const games = p.gameMetrics.reduce((s, g) => s + g.attempts, 0);
    const drawings = p.progressStats?.drawingsRecognizedTotal ?? 0;
    const music = p.musicPlays.reduce((s, m) => s + m.playCount, 0);
    const routines = p.subRoutines.length;
    return sum + comm + games + drawings + music + routines;
  }, 0);

  // Persiste no Firestore no máximo 1× a cada 10 min por evento, só após sync completo.
  useEffect(() => {
    if (profilesLoading) return;
    if (realParticipants === 0 && realRecords === 0) return;
    const statsKey = `conectar-event-stats-ts:${event.id}`;
    const lastUpdate = Number(localStorage.getItem(statsKey) ?? 0);
    if (Date.now() - lastUpdate < STATS_THROTTLE_MS) return;
    if (event.participantCount === realParticipants && event.recordCount === realRecords) return;
    localStorage.setItem(statsKey, String(Date.now()));
    syncEventStats(event.id, realParticipants, realRecords);
  }, [profilesLoading, realParticipants, realRecords, event.id, event.participantCount, event.recordCount, syncEventStats]);
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [collaboratorsExpanded, setCollaboratorsExpanded] = useState(false);

  const editable = canEditEvent(event);

  const handleDelete = () => {
    deleteEvent(event.id);
    navigate('/dashboard', { replace: true });
  };

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const resized = await resizeImageToDataUrl(file);
    updateEventPhoto(event.id, resized);
  };

  const lastSync = format(parseISO(event.lastSyncAt), "dd/MM/yyyy 'às' HH:mm", {
    locale: ptBR,
  });

  const dateRange =
    event.noPeriod || !event.startDate || !event.endDate
      ? 'Todos os dados do Firebase'
      : `${format(parseISO(event.startDate), 'dd/MM/yyyy', { locale: ptBR })} — ${format(parseISO(event.endDate), 'dd/MM/yyyy', { locale: ptBR })}`;
  const timeRange =
    !event.noPeriod && event.startTime && event.endTime
      ? `${event.startTime} — ${event.endTime}`
      : null;

  return (
    <div className="-mx-6 -mt-6 mb-8">
      <div className="relative w-full h-[180px] sm:h-[220px] md:h-[260px] lg:h-[300px] xl:h-[320px] overflow-hidden bg-muted">
        {event.photoUrl ? (
          <img
            src={event.photoUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-primary/10" />
        )}

        {editable && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhoto}
            />
            <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white text-xs font-medium px-3 py-1.5 backdrop-blur-sm transition-colors"
              >
                <ImagePlus className="h-3.5 w-3.5" />
                Trocar imagem
              </button>
              <EntityActionsMenu
                onEdit={() => navigate(`/dashboard/events/${event.id}/edit`)}
                onDelete={handleDelete}
                deleteTitle="Excluir evento"
                deleteDescription={`Tem certeza que deseja excluir "${event.title}"? Essa ação não pode ser desfeita.`}
              />
            </div>
          </>
        )}
      </div>

      <div className="px-6 pt-5">
        <span className="inline-flex items-center self-start rounded-full bg-primary px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground mb-3">
          {eventTypeLabel(event)}
        </span>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
          {event.title}
        </h2>
        <p className="flex items-center gap-1.5 text-muted-foreground text-sm mt-1.5">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          {event.school}
        </p>

        <div className="mt-5 rounded-xl border border-border bg-card p-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            <StatChip
              icon={<Calendar className="h-[18px] w-[18px]" />}
              label="Período"
              value={dateRange}
            />
            <StatChip
              icon={<Clock className="h-[18px] w-[18px]" />}
              label="Horário"
              value={timeRange ?? '—'}
            />
            <StatChip
              icon={<Users className="h-[18px] w-[18px]" />}
              label="Professores"
              value={roleCounts.professor.toLocaleString('pt-BR')}
            />
            <StatChip
              icon={<GraduationCap className="h-[18px] w-[18px]" />}
              label="Alunos"
              value={roleCounts.managed.toLocaleString('pt-BR')}
            />
            <StatChip
              icon={<Heart className="h-[18px] w-[18px]" />}
              label="Pai/Mãe"
              value={roleCounts.paiMae.toLocaleString('pt-BR')}
            />
            <StatChip
              icon={<Database className="h-[18px] w-[18px]" />}
              label="Registros"
              value={(realRecords > 0 ? realRecords : event.recordCount).toLocaleString('pt-BR')}
            />
          </div>

          <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <RefreshCw className="h-3.5 w-3.5 shrink-0" />
              Sincronizado em {lastSync}
            </div>
            <CollaboratorAvatarStack
              emails={event.collaboratorEmails}
              expanded={collaboratorsExpanded}
              onToggle={() => setCollaboratorsExpanded((v) => !v)}
            />
          </div>

          {collaboratorsExpanded && <CollaboratorList emails={event.collaboratorEmails} />}
        </div>
      </div>
    </div>
  );
}
